import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth, isGlobalAdmin } from '../hooks/useAuth';
import { toast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';

interface Team {
  teamId: string;
  projectId: string;
  name: string;
  description?: string;
  members: { userId: string; role: string; accessLevel: string }[];
  isDeleted?: boolean;
  deletedAt?: string;
}

interface Project {
  projectId: string;
  shortName: string;
}

interface TeamsResponse {
  items: Team[];
  archivedItems?: Team[];
}

interface ProjectsResponse {
  items: Project[];
  archivedItems?: Project[];
}

export default function Teams() {
  const api = useApi();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [showArchived, setShowArchived] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.get<TeamsResponse>('/api/teams'),
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<ProjectsResponse>('/api/projects'),
  });

  const restoreMutation = useMutation({
    mutationFn: (teamId: string) =>
      api.post(`/api/teams/${teamId}/restore`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      toast.success('Team restored successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to restore team');
    },
  });

  const handleRestore = async (team: Team) => {
    const confirmed = await confirm({
      title: 'Restore Team',
      message: `Restore "${team.name}"?`,
      confirmText: 'Restore',
      variant: 'info',
    });

    if (confirmed) {
      restoreMutation.mutate(team.teamId);
    }
  };

  const teams = data?.items || [];
  const archivedTeams = data?.archivedItems || [];
  const projects = projectsData?.items || [];
  const archivedProjects = projectsData?.archivedItems || [];
  const allProjects = [...projects, ...archivedProjects];

  // Create a map of projectId -> project name
  const projectNames = useMemo(() => {
    const map: Record<string, string> = {};
    allProjects.forEach(p => {
      map[p.projectId] = p.shortName;
    });
    return map;
  }, [allProjects]);

  // Group teams by project
  const teamsByProject = useMemo(() => {
    const grouped: Record<string, Team[]> = {};
    teams.forEach(team => {
      const projectId = team.projectId;
      if (!grouped[projectId]) {
        grouped[projectId] = [];
      }
      grouped[projectId].push(team);
    });
    // Sort projects by name
    return Object.entries(grouped).sort(([a], [b]) => {
      const nameA = projectNames[a] || 'Unknown';
      const nameB = projectNames[b] || 'Unknown';
      return nameA.localeCompare(nameB);
    });
  }, [teams, projectNames]);

  // Group archived teams by project
  const archivedTeamsByProject = useMemo(() => {
    const grouped: Record<string, Team[]> = {};
    archivedTeams.forEach(team => {
      const projectId = team.projectId;
      if (!grouped[projectId]) {
        grouped[projectId] = [];
      }
      grouped[projectId].push(team);
    });
    return Object.entries(grouped).sort(([a], [b]) => {
      const nameA = projectNames[a] || 'Unknown';
      const nameB = projectNames[b] || 'Unknown';
      return nameA.localeCompare(nameB);
    });
  }, [archivedTeams, projectNames]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Teams</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">All teams you have access to</p>
      </div>

      {/* Teams list grouped by project */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
        </div>
      ) : teams.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">No teams available</p>
          <Link to="/projects" className="btn-primary mt-4 inline-block">
            View Projects
          </Link>
        </div>
      ) : (
        <div className="space-y-8">
          {teamsByProject.map(([projectId, projectTeams]) => (
            <div key={projectId}>
              <div className="flex items-center gap-2 mb-4">
                <Link
                  to={`/projects/${projectId}`}
                  className="text-lg font-semibold text-gray-700 dark:text-gray-300 hover:text-primary-600"
                >
                  {projectNames[projectId] || 'Unknown Project'}
                </Link>
                <span className="text-sm text-gray-500 dark:text-gray-400">({projectTeams.length} {projectTeams.length === 1 ? 'team' : 'teams'})</span>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projectTeams.map((team) => (
                  <Link
                    key={team.teamId}
                    to={`/teams/${team.teamId}`}
                    className="card hover:shadow-md transition-shadow"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{team.name}</h3>
                    <p className="text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">{team.description || 'No description'}</p>
                    <div className="flex items-center gap-4 mt-4 text-sm text-gray-500 dark:text-gray-400">
                      <span>{team.members?.length || 0} members</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Archived Teams Section (Super Admin Only) */}
      {isGlobalAdmin(user) && archivedTeams.length > 0 && (
        <div className="mt-12">
          <button
            onClick={() => setShowArchived(!showArchived)}
            className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <svg
              className={`w-5 h-5 transition-transform ${showArchived ? 'rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <h2 className="text-lg font-semibold">Archived Teams ({archivedTeams.length})</h2>
          </button>

          {showArchived && (
            <div className="mt-4 space-y-6">
              {archivedTeamsByProject.map(([projectId, projectTeams]) => (
                <div key={projectId}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {projectNames[projectId] || 'Unknown Project'}
                    </span>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {projectTeams.map((team) => (
                      <div
                        key={team.teamId}
                        className="card bg-gray-100 dark:bg-dark-600 border-gray-300 dark:border-dark-500 opacity-75"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-500 dark:text-gray-400">{team.name}</h3>
                            <p className="text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{team.description || 'No description'}</p>
                          </div>
                          <span className="px-2 py-1 text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-full">
                            Archived
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-4">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Archived {team.deletedAt ? new Date(team.deletedAt).toLocaleDateString() : ''}
                          </span>
                          <button
                            onClick={() => handleRestore(team)}
                            disabled={restoreMutation.isPending}
                            className="text-sm font-medium text-primary-500 hover:text-primary-600"
                          >
                            Restore
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
